/**
 * EventDetailsModal - Модальное окно с деталями события календаря
 */

import React from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Badge,
  Divider,
  Button,
  Box,
} from '@mantine/core';
import {
  IconPhone,
  IconUsers,
  IconSchool,
  IconBuilding,
  IconClock,
  IconExternalLink,
  IconUser,
  IconMessage,
  IconCheck,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import tokens from '../../theme/designTokens';

const TYPE_CONFIG = {
  meeting: {
    label: 'Встреча',
    color: 'blue',
    icon: IconUsers,
  },
  call: {
    label: 'Звонок',
    color: 'green',
    icon: IconPhone,
  },
  master_class: {
    label: 'Мастер-класс',
    color: 'violet',
    icon: IconSchool,
  },
};

const STATUS_CONFIG = {
  planned: { label: 'Запланировано', color: 'blue' },
  completed: { label: 'Выполнено', color: 'green' },
  cancelled: { label: 'Отменено', color: 'gray' },
  missed: { label: 'Пропущено', color: 'red' },
  rescheduled: { label: 'Перенесено', color: 'orange' },
};

const formatDateTime = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

const EventDetailsModal = ({ opened, onClose, event }) => {
  const navigate = useNavigate();

  if (!event) return null;

  const typeConfig = TYPE_CONFIG[event.type] || TYPE_CONFIG.meeting;
  const statusConfig = STATUS_CONFIG[event.status] || STATUS_CONFIG.planned;
  const TypeIcon = typeConfig.icon;

  const handleOpenOrganization = () => {
    if (event.organization?.id) {
      navigate(`/organizations/${event.organization.id}`);
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <TypeIcon size={24} color={tokens.colors.brand.primary} />
          <Text fw={600}>{typeConfig.label}</Text>
        </Group>
      }
      size="md"
      radius="lg"
    >
      <Stack gap="md">
        {/* Организация */}
        <Group gap="xs">
          <IconBuilding size={18} color={tokens.colors.neutral[6]} />
          <Text
            fw={500}
            style={{ cursor: 'pointer', color: tokens.colors.brand.primary }}
            onClick={handleOpenOrganization}
          >
            {event.organization?.name}
          </Text>
          <IconExternalLink size={14} color={tokens.colors.brand.primary} />
        </Group>

        {/* Дата и время */}
        <Group gap="xs">
          <IconClock size={18} color={tokens.colors.neutral[6]} />
          <Text size="sm">
            {formatDateTime(event.start)}
            {event.end && ` — ${new Date(event.end).toLocaleTimeString('ru-RU', {
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'Europe/Moscow',
            })}`}
          </Text>
        </Group>

        {/* Статус */}
        <Group gap="xs">
          <Badge color={statusConfig.color} variant="light" radius="xl">
            {statusConfig.label}
          </Badge>
          <Badge color={typeConfig.color} variant="outline" radius="xl">
            {typeConfig.label}
          </Badge>
        </Group>

        <Divider />

        {/* Детали в зависимости от типа */}
        {event.type === 'meeting' && (
          <Stack gap="xs">
            {event.details?.participants?.length > 0 && (
              <Box>
                <Group gap={4} mb={4}>
                  <IconUser size={14} color={tokens.colors.neutral[6]} />
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Участники
                  </Text>
                </Group>
                <Text size="sm">{event.details.participants.join(', ')}</Text>
              </Box>
            )}
            {event.details?.contacts?.length > 0 && (
              <Box>
                <Group gap={4} mb={4}>
                  <IconUsers size={14} color={tokens.colors.neutral[6]} />
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Контакты
                  </Text>
                </Group>
                <Text size="sm">{event.details.contacts.join(', ')}</Text>
              </Box>
            )}
            {event.details?.comment && (
              <Box>
                <Group gap={4} mb={4}>
                  <IconMessage size={14} color={tokens.colors.neutral[6]} />
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Комментарий
                  </Text>
                </Group>
                <Text size="sm">{event.details.comment}</Text>
              </Box>
            )}
            {event.details?.summary && (
              <Box
                p="sm"
                style={{
                  backgroundColor: tokens.colors.semantic.success.background,
                  borderRadius: '8px',
                }}
              >
                <Group gap={4} mb={4}>
                  <IconCheck size={14} color={tokens.colors.semantic.success.primary} />
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Итоги встречи
                  </Text>
                </Group>
                <Text size="sm">{event.details.summary}</Text>
              </Box>
            )}
          </Stack>
        )}

        {event.type === 'call' && (
          <Stack gap="xs">
            {event.details?.subject && (
              <Box>
                <Group gap={4} mb={4}>
                  <IconMessage size={14} color={tokens.colors.neutral[6]} />
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Тема
                  </Text>
                </Group>
                <Text size="sm">{event.details.subject}</Text>
              </Box>
            )}
            {event.details?.contact && (
              <Box>
                <Group gap={4} mb={4}>
                  <IconUser size={14} color={tokens.colors.neutral[6]} />
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Контакт
                  </Text>
                </Group>
                <Text size="sm">{event.details.contact}</Text>
              </Box>
            )}
            {event.details?.caller && (
              <Box>
                <Group gap={4} mb={4}>
                  <IconPhone size={14} color={tokens.colors.neutral[6]} />
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Звонивший
                  </Text>
                </Group>
                <Text size="sm">{event.details.caller}</Text>
              </Box>
            )}
            {event.details?.result && (
              <Box
                p="sm"
                style={{
                  backgroundColor: tokens.colors.semantic.success.background,
                  borderRadius: '8px',
                }}
              >
                <Group gap={4} mb={4}>
                  <IconCheck size={14} color={tokens.colors.semantic.success.primary} />
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Результат
                  </Text>
                </Group>
                <Text size="sm">{event.details.result}</Text>
              </Box>
            )}
          </Stack>
        )}

        {event.type === 'master_class' && (
          <Stack gap="xs">
            <Group gap="xl">
              {event.details?.groups_count > 0 && (
                <Box>
                  <Text size="xs" c="dimmed" tt="uppercase" mb={4}>
                    Групп
                  </Text>
                  <Text size="lg" fw={600}>{event.details.groups_count}</Text>
                </Box>
              )}
              {event.details?.total_children > 0 && (
                <Box>
                  <Text size="xs" c="dimmed" tt="uppercase" mb={4}>
                    Детей
                  </Text>
                  <Text size="lg" fw={600}>{event.details.total_children}</Text>
                </Box>
              )}
            </Group>
            {event.details?.manager && (
              <Box>
                <Group gap={4} mb={4}>
                  <IconUser size={14} color={tokens.colors.neutral[6]} />
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Менеджер
                  </Text>
                </Group>
                <Text size="sm">{event.details.manager}</Text>
              </Box>
            )}
            {event.details?.sales && (
              <Box>
                <Group gap={4} mb={4}>
                  <IconUser size={14} color={tokens.colors.neutral[6]} />
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Сейл
                  </Text>
                </Group>
                <Text size="sm">{event.details.sales}</Text>
              </Box>
            )}
            {event.details?.comment && (
              <Box>
                <Group gap={4} mb={4}>
                  <IconMessage size={14} color={tokens.colors.neutral[6]} />
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Комментарий
                  </Text>
                </Group>
                <Text size="sm">{event.details.comment}</Text>
              </Box>
            )}
            {event.details?.result_comment && (
              <Box
                p="sm"
                style={{
                  backgroundColor: tokens.colors.semantic.success.background,
                  borderRadius: '8px',
                }}
              >
                <Group gap={4} mb={4}>
                  <IconCheck size={14} color={tokens.colors.semantic.success.primary} />
                  <Text size="xs" c="dimmed" tt="uppercase">
                    Результат
                  </Text>
                </Group>
                <Text size="sm">{event.details.result_comment}</Text>
              </Box>
            )}
          </Stack>
        )}

        <Divider />

        {/* Кнопки действий */}
        <Group justify="flex-end">
          <Button variant="light" onClick={onClose}>
            Закрыть
          </Button>
          <Button onClick={handleOpenOrganization}>
            Открыть организацию
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default EventDetailsModal;
